import React, {useState} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  ConnectionTier,
  type IntegrationConnection,
} from '../../types/generated/graphql';
import {IntegrationLogo} from '../../integrations';

interface Props {
  connection: IntegrationConnection;
  onDisconnect: (connectionId: string) => void;
  onReauth: (integrationId: string) => void;
  disconnecting?: boolean;
  /**
   * S8: when true the card hides Reconnect / Revoke and renders as
   * status-only. Used by the Org Integrations page when the viewer
   * lacks the admin role; server-side `requireOrgAdmin` is the
   * actual gate.
   */
  readonly?: boolean;
  /**
   * S7: render the "last used" / "expires" / "shared with N orgs"
   * metadata under the card header. The default OFF keeps Org-pane
   * cards compact; the My Connections page opts in.
   */
  showMetadata?: boolean;
  /**
   * S9: when true and the row is a RUN-tier ACTIVE connection,
   * render a "Save permanently" CTA that fires
   * `promoteConnectionToUser`. The parent page is responsible for
   * checking the integration manifest's `allowedTiers` includes
   * USER before passing this through — the server still validates
   * the gate, but suppressing it client-side avoids dangling CTAs
   * for RUN-only integrations like the Plaid one-shot.
   */
  canPromoteToUser?: boolean;
  onPromoteToUser?: (connectionId: string) => void;
  promoting?: boolean;
}

const STATUS_CONFIG: Record<string, {color: 'success' | 'error' | 'warning' | 'default'; label: string}> = {
  ACTIVE: {color: 'success', label: 'Connected'},
  ERROR: {color: 'error', label: 'Error'},
  EXPIRED: {color: 'warning', label: 'Expired'},
  DISCONNECTED: {color: 'default', label: 'Disconnected'},
  REVOKED: {color: 'default', label: 'Revoked'},
};

function tierLabel(tier: ConnectionTier): string {
  switch (tier) {
    case ConnectionTier.Platform:
      return 'Platform';
    case ConnectionTier.Org:
      return 'Organization';
    case ConnectionTier.User:
      return 'Personal';
    case ConnectionTier.Run:
      return 'Run (ephemeral)';
    default:
      return 'Unknown';
  }
}

function formatRelative(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  const seconds = Math.round(diffMs / 1000);
  if (Math.abs(seconds) < 60) return seconds <= 0 ? 'just now' : `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export const ConnectionCard: React.FC<Props> = ({
  connection,
  onDisconnect,
  onReauth,
  disconnecting,
  readonly = false,
  showMetadata = false,
  canPromoteToUser = false,
  onPromoteToUser,
  promoting = false,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const statusCfg = STATUS_CONFIG[connection.status] ?? STATUS_CONFIG.ACTIVE;
  const needsReauth =
    connection.status === 'ERROR' || connection.status === 'EXPIRED';
  const terminal =
    connection.status === 'REVOKED' || connection.status === 'DISCONNECTED';

  const lastUsed = formatRelative(connection.lastUsedAt);
  const sharedOrgCount = connection.usedByOrgIds?.length ?? 0;
  const expires = formatRelative(connection.expiresAt);
  const isRun = connection.tier === ConnectionTier.Run;

  return (
    <>
      <Card variant="outlined" sx={{mb: 1.5}}>
        <CardContent sx={{display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': {pb: 1.5}}}>
          <IntegrationLogo
            integrationId={connection.integrationId}
            name={connection.provider}
            size={28}
          />
          <Box sx={{flex: 1, minWidth: 0}}>
            <Typography variant="subtitle2" noWrap>
              {connection.provider}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {connection.integrationId} &middot; {tierLabel(connection.tier)}
            </Typography>
            {showMetadata && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{display: 'block', mt: 0.25, fontStyle: 'italic'}}
              >
                {lastUsed ? `Last used ${lastUsed}` : 'Never used'}
                {sharedOrgCount > 0 &&
                  ` · Used by ${sharedOrgCount} org${sharedOrgCount === 1 ? '' : 's'}`}
                {isRun && expires && ` · Expires ${expires}`}
              </Typography>
            )}
          </Box>

          <Chip
            size="small"
            label={statusCfg.label}
            color={statusCfg.color}
            icon={needsReauth ? <ErrorOutlineIcon /> : <CheckCircleOutlineIcon />}
          />

          {needsReauth && !readonly && (
            <Tooltip title="Re-authenticate">
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => onReauth(connection.integrationId)}
              >
                Reconnect
              </Button>
            </Tooltip>
          )}

          {!terminal &&
            !readonly &&
            isRun &&
            canPromoteToUser &&
            connection.status === 'ACTIVE' &&
            onPromoteToUser && (
              <Tooltip title="Promote this run-only connection to a permanent personal connection">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onPromoteToUser(connection.id)}
                    disabled={promoting}
                  >
                    {promoting ? 'Saving…' : 'Save permanently'}
                  </Button>
                </span>
              </Tooltip>
            )}

          {!terminal && !readonly && (
            <Tooltip title="Revoke">
              <IconButton
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={disconnecting}
              >
                <LinkOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Revoke {connection.provider}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will revoke access to {connection.provider} at the{' '}
            {tierLabel(connection.tier).toLowerCase()} tier. The remote token
            is deleted and the connection cannot be restored — reconnecting
            will create a new connection.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              onDisconnect(connection.id);
            }}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
